import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * Keeps the downloads list in step with the selected variant.
 *
 * Spec and install sheets are product-level, but IES files are not: several
 * manufacturers in this catalog publish finish-specific photometry (SCM10 and
 * FX8BLD both do), so the list has to change when the variant does.
 *
 * The variant picker only morphs itself on a same-product change, so this
 * follows the product-price.js pattern — take the re-rendered section HTML off
 * the productSelect event and swap our own list out of it.
 *
 * @extends {Component}
 */
class ProductDownloads extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /** @param {ProductSelectEvent} event */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;
        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const next = html.querySelector(`product-downloads[data-block-id="${this.dataset.blockId}"]`);
        if (!next) return;

        // The whole body is replaced rather than diffed: a variant change can
        // add or remove rows, not just relabel them, so there is nothing stable
        // to morph against.
        this.innerHTML = next.innerHTML;
        // An empty variant means no documents at all — collapse rather than
        // leaving an orphaned heading behind.
        this.hidden = next.hidden;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-downloads] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-downloads')) {
  customElements.define('product-downloads', ProductDownloads);
}
